/**
 * Composición de la aplicación: rutas, tema y avisos globales.
 *
 * Sin lógica de negocio: las reglas viven en `domain/reglas`, los datos en
 * `persistencia/` y el contenido en `curriculum/`.
 *
 * El modo foco vive FUERA del layout: sin barra de navegación y con su propia
 * URL, para que recargar durante una sesión vuelva a la sesión y no a Hoy.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { Navigate, Route, Routes } from 'react-router-dom';

import { sesionEnProgreso } from '../domain/reglas/progresion';
import { ajustesRepo } from '../persistencia/ajustesRepo';
import { sesionesRepo } from '../persistencia/sesionesRepo';
import { ModoFoco } from '../ui/foco/ModoFoco';
import { AppLayout } from '../ui/layout/AppLayout';
import { Onboarding } from '../ui/onboarding/Onboarding';
import { Ajustes } from '../ui/pantallas/Ajustes';
import { Banco } from '../ui/pantallas/Banco';
import { Herramientas } from '../ui/pantallas/Herramientas';
import { Hoy } from '../ui/pantallas/Hoy';
import { Plan } from '../ui/pantallas/Plan';
import { Comparacion } from '../ui/pantallas/Comparacion';
import { Progreso } from '../ui/pantallas/Progreso';
import { ActualizacionPWA } from './ActualizacionPWA';
import { AvisoEscrituraFallida } from './AvisoEscrituraFallida';
import { RUTA_AJUSTES, RUTA_SESION } from './rutas';
import { useTema } from './useTema';

export function App() {
  const ajustes = useLiveQuery(() => ajustesRepo.obtener(), [], undefined);
  const sesiones = useLiveQuery(() => sesionesRepo.listar(), [], undefined);
  useTema(ajustes?.tema);

  const haySesionEnProgreso = Boolean(sesiones && sesionEnProgreso(sesiones));

  /**
   * Mientras se lee la base no se pinta la interfaz: mostrarla y taparla
   * después con el onboarding sería un parpadeo en cada arranque.
   *
   * Pero el contenido se decide DENTRO del árbol, no con un `return`
   * temprano. El registro del service worker vive más abajo, y con un return
   * antes de tiempo no llegaba a montarse: quien abría la aplicación por
   * primera vez se quedaba sin service worker —y por tanto sin instalación ni
   * offline— hasta terminar el onboarding. Bug real, visible solo sobre
   * HTTPS.
   */
  const contenido = !ajustes ? null : !ajustes.onboardingVisto ? (
    <Onboarding />
  ) : (
    <Routes>
      {/* Modo foco: pantalla completa, sin navegación. */}
      <Route path={RUTA_SESION} element={<ModoFoco />} />

      <Route element={<AppLayout />}>
        <Route path="/" element={<Hoy />} />
        <Route path="/plan" element={<Plan />} />
        <Route path="/banco" element={<Banco />} />
        <Route path="/progreso" element={<Progreso />} />
        {/* La comparación se abre DESDE Progreso; no es un sexto destino de
            la navegación, que tiene exactamente 5 y nunca más. */}
        <Route path="/progreso/comparacion" element={<Comparacion />} />
        <Route path="/herramientas" element={<Herramientas />} />
        <Route path={RUTA_AJUSTES} element={<Ajustes />} />
        {/* Cualquier ruta desconocida vuelve a Hoy en vez de dejar una pantalla en blanco. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );

  return (
    <>
      {contenido}
      <AvisoEscrituraFallida />
      {/* Siempre montado, pase lo que pase con el contenido: es lo que
          registra el service worker. */}
      <ActualizacionPWA sesionEnProgreso={haySesionEnProgreso} />
    </>
  );
}
